import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import type {
  UsageAggregationResponse,
  UsageInfo,
  UsageLinesResponse,
  UsageTarget,
} from "../types/usage.ts";
import { useApi } from "./use-api.ts";

/**
 * Hook to fetch current usage info.
 */
export function useUsageInfo() {
  const { callApi } = useApi<UsageInfo>();

  return useQuery({
    queryKey: ["usage"],
    queryFn: async () => callApi("/usage"),
    refetchInterval: 30_000,
  });
}

/**
 * Hook to fetch paginated usage lines.
 */
export function useUsageLines(options?: { initialLimit?: number; target?: UsageTarget }) {
  const { callApi } = useApi<UsageLinesResponse>();
  const [offset, setOffset] = useState(0);
  const [limit] = useState(options?.initialLimit ?? 50);
  const [target, setTarget] = useState<UsageTarget | undefined>(options?.target);

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    params.set("offset", offset.toString());
    params.set("limit", limit.toString());
    if (target) {
      params.set("target", target);
    }
    return `/usage/lines?${params.toString()}`;
  }, [offset, limit, target]);

  const query = useQuery({
    queryKey: ["usage-lines", offset, limit, target],
    queryFn: async () => callApi(queryString),
    refetchInterval: false,
  });

  const loadMore = useCallback(() => {
    if (query.data?.has_more) {
      setOffset((prev) => prev + limit);
    }
  }, [query.data?.has_more, limit]);

  const loadPrevious = useCallback(() => {
    setOffset((prev) => Math.max(0, prev - limit));
  }, [limit]);

  const goToPage = useCallback(
    (page: number) => {
      setOffset(page * limit);
    },
    [limit],
  );

  return {
    ...query,
    offset,
    limit,
    target,
    setTarget,
    loadMore,
    loadPrevious,
    goToPage,
    currentPage: Math.floor(offset / limit),
    totalPages: query.data ? Math.ceil(query.data.total / limit) : 0,
  };
}

/**
 * Timeframe presets for usage graph.
 */
export type TimeframePreset =
  | "today"
  | "this_week"
  | "this_month"
  | "last_3_months"
  | "last_year"
  | "all_time";

/**
 * Get granularity and periods for a timeframe preset.
 */
function getTimeframeConfig(preset: TimeframePreset): {
  granularity: "day" | "week" | "month";
  periods: number;
} {
  switch (preset) {
    case "today":
      return { granularity: "day", periods: 1 };
    case "this_week":
      return { granularity: "day", periods: 7 };
    case "this_month":
      return { granularity: "day", periods: 30 };
    case "last_3_months":
      return { granularity: "week", periods: 12 };
    case "last_year":
      return { granularity: "month", periods: 12 };
    case "all_time":
      return { granularity: "month", periods: 365 };
  }
}

/**
 * Hook to fetch usage aggregations for graphs.
 */
export function useUsageAggregations(options?: {
  granularity?: "day" | "week" | "month";
  periods?: number;
  groupBy?: "target" | null;
  timeframe?: TimeframePreset;
}) {
  const { callApi } = useApi<UsageAggregationResponse>();
  const [timeframe, setTimeframe] = useState<TimeframePreset>(options?.timeframe ?? "this_month");
  const [groupBy, setGroupBy] = useState<"target" | null>(options?.groupBy ?? null);

  // Derive granularity and periods from timeframe
  const { granularity, periods } = useMemo(() => getTimeframeConfig(timeframe), [timeframe]);

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    params.set("granularity", granularity);
    params.set("periods", periods.toString());
    if (groupBy) {
      params.set("group_by", groupBy);
    }
    return `/usage/aggregations?${params.toString()}`;
  }, [granularity, periods, groupBy]);

  const query = useQuery({
    queryKey: ["usage-aggregations", granularity, periods, groupBy],
    queryFn: async () => callApi(queryString),
    refetchInterval: false,
  });

  return {
    ...query,
    granularity,
    periods,
    groupBy,
    setGroupBy,
    timeframe,
    setTimeframe,
  };
}
